import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Formik, Form, useFormikContext, useField, FormikProps } from 'formik'
import { Box, Image } from 'rebass'
import * as yup from 'yup'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'
import { theme } from 'theme'
import ButtonPrimary from 'components/Shared/Button/OldButton/ButtonPrimary'
import {
    ButtonCheckSquareActive,
    ButtonEditOrange,
    ButtonXSquareRed,
    StyledButtonCheckSquare,
    StyledButtonEdit,
    StyledButtonXSquare,
} from 'components/ButtonFe'
import Inputs from 'components/Inputs'
import { selectEditTagGroupOptions, TagOption, selectRejectionTagGroupOptions } from 'state/constants'
import ErrorHandleImg from 'icons/ErrorHandle.png'
import { parseBackendDate, convertToBackendStr, dateIsInPast } from 'services/dates'
import {
    makeNextStepSelector,
    makeSelectedMediaContentsSelector,
    selectSelectedCampaignUuid,
    setSelectedMediaContent,
    SubmissionProgressProps,
    setReviewModalType,
    setReviewModalOpen,
    REVIEW_MODAL_TYPE,
    setConfirmRejectDataInWaitingList,
    selectSelectedDecisionEditor,
    setSelectedDecisionEditor,
    DECISION_EDITOR_ENUM,
    setSelectedReviewData,
    selectSelectedReviewData,
    clearSelectedReviewData,
    selectSelectedSubmissionView,
    selectSelectedCampaignSummary,
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSidebarStep,
    selectSubmissionListParams,
    selectRewriteCaptionReviewProgressUuids,
    selectRetryCaptionReviewProgressUuids,
} from 'state/review'
import { useDispatch, useSelector } from 'react-redux'
import { SUBMISSION_VIEW_ENUM, approveSubmission, rejectSubmission, requestEditsForSubmission } from 'state/submissions'
import { State } from 'state'
import { DisplayGroupedOption } from 'state/constants'
import { StepState, TaskState } from 'state/tasks'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { POST_TIME_WINDOW_SCHEMA } from '../../validationSchema'
import RectangleButton from 'particles/Button/RectangleButton'
import spacing from 'particles/styles/spacing'
import { coreColors } from 'particles/colors'
import Text from 'particles/Text'
import { getFieldErrorNames } from 'components/utils/errors'
import styled, { CSSProperties } from 'styled-components'
import { Dropdown } from '../VerifyTable'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'
import RangeInput from 'particles/RangeInput'
import { ContentData } from 'services/cloudinary'
import AiReview from 'components/Submissions/Review/common/AiReview'
import TextArea from 'components/Submissions/Review/common/AiReview/TextArea'
import Restricted from 'components/Shared/Permission/Restricted'
import { CampaignSubmission } from 'components/Shared/Permission/enums'

interface ReviewSubmission {
    submissionUuid?: string
    step: StepState
    task: TaskState
    submissionProgress: SubmissionProgressProps
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
    isInsideBatch: boolean
}

const ReviewSubmission: React.FC<ReviewSubmission> = ({
    submissionUuid,
    step,
    task,
    submissionProgress,
    handleEditTagChange,
    handleRejectTagChange,
    isInsideBatch,
}) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const decisionEditor = useSelector(selectSelectedDecisionEditor(submissionProgress.uuid))
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const rewriteProgressUuids = useSelector(selectRewriteCaptionReviewProgressUuids)
    const retryProgressUuids = useSelector(selectRetryCaptionReviewProgressUuids)

    const setDecisionEditor = (type: DECISION_EDITOR_ENUM): void => {
        dispatch(setSelectedDecisionEditor({ uuid: submissionProgress.uuid, type }))
    }

    const selectNextStep = useMemo(makeNextStepSelector, [])
    const nextStep = useSelector((_: any) => selectNextStep(_, { step, task }))

    const isPastDeadline =
        selectedCampaignSummary && dateIsInPast(step?.time_window?.end, selectedCampaignSummary.country)
    const isPastNextDeadline =
        nextStep && selectedCampaignSummary
            ? dateIsInPast(nextStep.time_window.end, selectedCampaignSummary.country)
            : false

    const disableEditAndRejectTab =
        rewriteProgressUuids.includes(submissionProgress.uuid) || retryProgressUuids.includes(submissionProgress.uuid)
    return (
        <>
            <FlexBox gap={spacing[300]} marginBottom={spacing[200]}>
                <Box>
                    {decisionEditor === DECISION_EDITOR_ENUM.APPROVE ? (
                        <ButtonCheckSquareActive
                            width={126}
                            height={50}
                            paddingBottom="5px"
                            iconSize={16}
                            text={newT('button.approve')}
                        />
                    ) : (
                        <StyledButtonCheckSquare
                            width={126}
                            height={45}
                            iconSize={16}
                            onClick={() => setDecisionEditor(DECISION_EDITOR_ENUM.APPROVE)}
                            text={newT('button.approve')}
                        />
                    )}
                </Box>
                <Box>
                    {decisionEditor === DECISION_EDITOR_ENUM.REQUEST_EDITS ? (
                        <ButtonEditOrange
                            width={126}
                            height={50}
                            paddingBottom="5px"
                            iconSize={16}
                            text={newT('button.requestEdits')}
                        />
                    ) : (
                        <StyledButtonEdit
                            width={126}
                            height={45}
                            iconSize={16}
                            onClick={() => setDecisionEditor(DECISION_EDITOR_ENUM.REQUEST_EDITS)}
                            text={newT('button.requestEdits')}
                            disabled={disableEditAndRejectTab}
                        />
                    )}
                </Box>
                <Box>
                    {decisionEditor === DECISION_EDITOR_ENUM.REJECT ? (
                        <ButtonXSquareRed
                            width={126}
                            height={50}
                            paddingBottom="5px"
                            iconSize={16}
                            text={newT('button.reject')}
                        />
                    ) : (
                        <StyledButtonXSquare
                            width={126}
                            height={45}
                            iconSize={16}
                            onClick={() => setDecisionEditor(DECISION_EDITOR_ENUM.REJECT)}
                            text={newT('button.reject')}
                            disabled={disableEditAndRejectTab}
                        />
                    )}
                </Box>
            </FlexBox>
            <Box>
                {decisionEditor === DECISION_EDITOR_ENUM.APPROVE ? (
                    isPastNextDeadline ? (
                        <>
                            <Text scale="bodyL700">
                                {newT('submission.review.approveSubmissionHeader', 'Approve This Submission')}
                            </Text>
                            <Box sx={{ textAlign: 'center' }} my={theme.spacing.m}>
                                <Image src={ErrorHandleImg} />
                            </Box>
                            <Box my={theme.spacing.xxs} mx={theme.spacing.s}>
                                <Text color="error" scale="caption400" style={{ textAlign: 'center' }}>
                                    {newT(
                                        'submission.review.deadlineExpiredInstruction',
                                        "The Partiposter will not be able to progress to the next step because it's deadline is over.",
                                    )}
                                </Text>
                            </Box>
                            <Box mb={theme.spacing.l} mx={theme.spacing.xxs}>
                                <Text scale="caption400" style={{ textAlign: 'center' }}>
                                    {newT(
                                        'submission.review.contactAccountManagerInstruction',
                                        'Please contact your Account Manager to extend the schedule and allow for resubmissions.',
                                    )}
                                </Text>
                            </Box>
                        </>
                    ) : (
                        <ApproveSubmission
                            submissionUuid={submissionUuid}
                            task={task}
                            step={step}
                            submissionProgress={submissionProgress}
                        />
                    )
                ) : null}
                {decisionEditor === DECISION_EDITOR_ENUM.REQUEST_EDITS ? (
                    isPastDeadline ? (
                        <>
                            <Text scale="bodyL700" textAlign="left">
                                {newT(
                                    'submission.review.requestEditForSubmissionTitle',
                                    'Request Edits this submission',
                                )}
                            </Text>
                            <Box sx={{ textAlign: 'center' }} my={theme.spacing.m}>
                                <Image src={ErrorHandleImg} />
                            </Box>

                            <Box my={theme.spacing.xxs} mx={theme.spacing.s}>
                                <Text color="error" scale="caption400" style={{ textAlign: 'center' }}>
                                    {newT(
                                        'errorMessage.businessLogic.cantRequestEditAfterDeadline',
                                        'The Partiposter will not be able to resubmit content because the schedule for the current step is over.',
                                    )}
                                </Text>
                            </Box>

                            <Box mb={theme.spacing.l} mx={theme.spacing.xxs}>
                                <Text scale="caption400" style={{ textAlign: 'center' }}>
                                    {newT(
                                        'submission.review.contactAccountManagerInstruction',
                                        'Please contact your Account Manager to extend the schedule and allow for resubmissions.',
                                    )}
                                </Text>
                            </Box>
                        </>
                    ) : (
                        <EditSubmission
                            task={task}
                            submissionUuid={submissionUuid}
                            submissionProgress={submissionProgress}
                            handleEditTagChange={handleEditTagChange}
                        />
                    )
                ) : null}
                {decisionEditor === DECISION_EDITOR_ENUM.REJECT && (
                    <RejectSubmission
                        task={task}
                        submissionUuid={submissionUuid}
                        submissionProgress={submissionProgress}
                        handleRejectTagChange={handleRejectTagChange}
                        isInsideBatch={isInsideBatch}
                    />
                )}
            </Box>
        </>
    )
}

export const FileSelector: React.FC<any> = ({ data, ...props }) => {
    const [field] = useField(props)
    const { setFieldValue } = useFormikContext()
    const dispatch = useDispatch()
    const onDragEnd = (reorderedValues: any) => {
        dispatch(setSelectedMediaContent(reorderedValues.map((file: any) => file.uuid)))
    }

    useEffect(() => {
        const newFiles: any = []
        field.value.forEach((file: any) => {
            if (data.findIndex((selectedFile: any) => selectedFile.uuid === file.uuid) > -1) {
                newFiles.push(file)
            }
        })

        setFieldValue(field.name as never, _.uniqBy([...newFiles, ...data], 'uuid'))
    }, [data])

    return (
        <Inputs.OrderedContentListInWindowInput
            valueMapper={(value: any): { resource_type: string; public_id: string } => ({
                resource_type: value.resource_type,
                public_id: value.public_id,
            })}
            setList={onDragEnd}
            width={426}
            {...props}
        />
    )
}

// Will save state once the form have been destroyed
interface AutoSaveReviewDataProps {
    uuid: string
    decisionEditor: DECISION_EDITOR_ENUM
}

export interface FormikValues {
    comment?: string
    data: {
        post_time_window: {
            start?: string
            end?: string
        } | null
        files: ContentData[]
        caption: string
    }
}

const AutoSaveReviewData: React.FC<AutoSaveReviewDataProps> = ({ uuid, decisionEditor }) => {
    const dispatch = useDispatch()
    const reviewData = useRef<any>({})
    const { values, dirty } = useFormikContext()

    useEffect(() => {
        reviewData.current = values
    }, [values])
    useEffect(() => {
        return () => {
            dirty && dispatch(setSelectedReviewData({ uuid, decisionEditor, data: reviewData.current }))
        }
    }, [dirty])
    return null
}

export const ScrollToError = () => {
    const { errors, isSubmitting, isValidating } = useFormikContext()

    useEffect(() => {
        if (isSubmitting && !isValidating) {
            const errorFieldNames = getFieldErrorNames(errors)
            if (errorFieldNames.length > 0) {
                const selector = `[name='${errorFieldNames[0]}']`
                const errorElement = document.querySelector(selector)
                errorElement?.scrollIntoView({ block: 'nearest' })
            }
        }
    }, [errors, isSubmitting, isValidating])

    return null
}

const ApproveSubmission: React.FC<any> = ({ submissionUuid, task, step, submissionProgress }) => {
    const showDynamicLinkInput = task && task.data && task.data.link_in_bio_type == 2
    const showPostTimeInput = task && task.data && task.data.add_post_time

    const showCaptionInput = [
        STEP_TYPE_ENUM.SUBMIT_CONTENT_FB_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL,
    ].includes(step.type)
    const showFileInput = [
        STEP_TYPE_ENUM.SUBMIT_CONTENT_FB_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_STORY,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST,
        STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL,
    ].includes(step.type)

    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedCampaignUuid = campaignSummary?.uuid
    const selectSelectedMediaContents = useMemo(makeSelectedMediaContentsSelector, [])
    const selectedContent = useSelector((state: State) =>
        selectSelectedMediaContents(state, { submissionProgress, defaultFirstContent: true }),
    )
    const selectedReviewData = useSelector(
        selectSelectedReviewData(submissionProgress.uuid, DECISION_EDITOR_ENUM.APPROVE),
    )
    const activePageNum = useSelector(selectActivePageNum)
    const selectedStep = useSelector(selectSidebarStep)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const rewriteProgressUuids = useSelector(selectRewriteCaptionReviewProgressUuids)
    const retryProgressUuids = useSelector(selectRetryCaptionReviewProgressUuids)

    const handleApprove = useCallback(
        (comment: any, data: any) => {
            if (selectedCampaignUuid) {
                dispatch(
                    approveSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionProgressUuid: submissionProgress.uuid,
                        comment,
                        data,
                        getSubmissionListPayload: {
                            offset: activePageNum * MAX_PAGE_SIZE,
                            stepUuid: selectedStep?.uuid,
                            ...submissionListParams,
                        },
                    }),
                )
                dispatch(clearSelectedReviewData(submissionProgress.uuid))
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )

    const initialValues = useMemo(() => {
        if (selectedReviewData) {
            return selectedReviewData
        }
        const initialDataValues: any = { ...submissionProgress.data }

        if (showCaptionInput) {
            initialDataValues.caption = submissionProgress.data.caption
        }
        if (showFileInput) {
            initialDataValues.files = selectedContent != null ? selectedContent : []
        }
        if (showDynamicLinkInput) {
            initialDataValues.link_in_bio_url = ''
        }
        if (showPostTimeInput) {
            initialDataValues.post_time_window = null
        }

        return {
            comment: '',
            data: initialDataValues,
        }
    }, [
        selectedReviewData,
        submissionProgress,
        showCaptionInput,
        showFileInput,
        showDynamicLinkInput,
        showPostTimeInput,
        selectedContent,
    ])

    const validationSchema = useMemo(() => {
        const dataValidation: any = {}
        if (showCaptionInput) {
            dataValidation.caption = yup
                .string()
                .nullable()
                .max(2200, newT('errorMessage.validation.maxCharacterLimit', 'Max. 2,200 characters'))
                .required(newT('errorMessage.validation.enterCaption', 'Please enter a caption for the Partiposter'))
        }
        if (showFileInput) {
            dataValidation.files = yup
                .array()
                .of(yup.string())
                .min(
                    1,
                    newT(
                        'errorMessage.validation.selectPhotoForApproval',
                        'Please select at least one photo to approve',
                    ),
                )
                .required(
                    newT(
                        'errorMessage.validation.selectPhotoForApproval',
                        'Please select at least one photo to approve',
                    ),
                )
        }
        if (showDynamicLinkInput) {
            dataValidation.link_in_bio_url = yup
                .string()
                .nullable()
                .url(newT('errorMessage.validation.enterUrlLink', 'Please enter a valid URL for the link in bio'))
        }
        if (showPostTimeInput && campaignSummary) {
            dataValidation.post_time_window = POST_TIME_WINDOW_SCHEMA(task, campaignSummary.country)
        }
        return yup.object({
            comment: yup
                .string()
                .nullable()
                .max(10000, ({ max }) =>
                    newT(
                        'errorMessage.validation.enterLessThanCharacterLimit',
                        'Please enter less than {{max}} characters.',
                        {
                            max,
                        },
                    ),
                )
                .required(newT('errorMessage.validation.enterComment', 'Please enter comment.')),
            data: yup.object(dataValidation),
        })
    }, [showCaptionInput, showFileInput, showDynamicLinkInput, showPostTimeInput, task])

    const onDateChange = (handleChange: any) => (date: Date | null) => {
        handleChange(date ? parseBackendDate(date.toISOString(), undefined, { showTime: true }) : '')
    }

    const removeEmptyOrNull = (obj: any) => {
        Object.keys(obj).forEach(
            k =>
                (obj[k] && typeof obj[k] === 'object' && removeEmptyOrNull(obj[k])) ||
                (!obj[k] && obj[k] !== undefined && delete obj[k]),
        )

        return obj
    }
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values: FormikValues, actions) => {
                // Cloning the values prevents displaying the backend format string in the date input after submission.
                const dataCopy = { ...values.data }
                const data = removeEmptyOrNull(dataCopy)
                if (
                    campaignSummary &&
                    data.post_time_window &&
                    data.post_time_window.start &&
                    data.post_time_window.end
                ) {
                    data.post_time_window = {
                        start: convertToBackendStr(data.post_time_window.start, campaignSummary.country),
                        end: convertToBackendStr(data.post_time_window.end, campaignSummary.country),
                    }
                } else {
                    delete data.post_time_window
                }
                handleApprove(values.comment, data)
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<FormikValues>) => (
                <Form>
                    <ScrollableBox>
                        <Text scale="bodyL700">
                            {newT('submission.review.approveSubmissionHeader', 'Approve This Submission')}
                        </Text>
                        {showFileInput ? (
                            <>
                                {formikBag.values.data.files.length == 0 && (
                                    <Text scale="caption400">
                                        {newT(
                                            'submission.review.choosePhotoToApproveInstruction',
                                            'Choose at least one photo from the photo list',
                                        )}
                                    </Text>
                                )}
                                {formikBag.values.data.files.length == 1 && (
                                    <Text scale="caption400">
                                        {newT(
                                            'submission.review.arrangeApprovedPhotosOrderInstruction',
                                            'Click and drag to rearrange the order of the approved photos',
                                        )}
                                    </Text>
                                )}
                                <FileSelector name="data.files" data={selectedContent} dragPosX={755} dragPosY={279} />
                            </>
                        ) : (
                            <Box py={spacing[300]} px={spacing[600]}>
                                <Text color="action" scale="caption400" textAlign="center">
                                    {newT(
                                        'submission.review.approvePhotosVideosInstruction',
                                        'You will be approving all photos/videos in this submission.',
                                    )}
                                </Text>
                            </Box>
                        )}
                        {showCaptionInput && (
                            <>
                                <TextArea submissionProgress={submissionProgress} name="data.caption" />
                                <Restricted permissions={[CampaignSubmission.ReviewCaptionWithAi]}>
                                    <AiReview reviewMode={'Approval'} submissionProgress={submissionProgress} />
                                </Restricted>
                            </>
                        )}
                        {showDynamicLinkInput && (
                            <>
                                <Text scale="bodyL700" textAlign="left">
                                    {newT(
                                        'submission.review.enterPartiposterLinkInBioHeader',
                                        "Add Partiposter's unique link in bio",
                                    )}
                                </Text>
                                <Text scale="caption400" textAlign="left">
                                    {newT(
                                        'submission.review.enterPartiposterLinkInBioSubtext',
                                        'The Partiposter will be posting this link to their profile.',
                                    )}
                                </Text>
                                <Inputs.TextArea
                                    className="expanding-input"
                                    name="data.link_in_bio_url"
                                    placeholder={newT(
                                        'placeholderText.textField.websiteExample',
                                        'e.g. http://www.partipost.com',
                                    )}
                                    minTextAreaHeight={64}
                                />
                            </>
                        )}
                        {showPostTimeInput && (
                            <RangeInput.Date
                                label={newT('submission.review.schedulePostTitle')}
                                subtext={newT('submission.review.schedulePostSubtitle')}
                                firstRangeName="data.post_time_window.start"
                                secondRangeName="data.post_time_window.end"
                                firstRangePlaceholder={newT('general.information.startDate')}
                                secondRangePlaceholder={newT('general.information.endDate')}
                                firstRangeOnChange={onDateChange(formikBag.handleChange('data.post_time_window.start'))}
                                secondRangeOnChange={onDateChange(formikBag.handleChange('data.post_time_window.end'))}
                                firstRangeDate={
                                    formikBag.values.data.post_time_window
                                        ? formikBag?.values?.data?.post_time_window?.start
                                        : undefined
                                }
                                secondRangeDate={
                                    formikBag?.values?.data?.post_time_window
                                        ? formikBag?.values?.data?.post_time_window?.end
                                        : undefined
                                }
                                size="s"
                                showTime
                                inputWidth="160px"
                                onClickClear={name => name && formikBag.setFieldValue(name, '')}
                                firstRangePopperPlacement="bottom-start"
                                secondRangePopperPlacement="bottom-end"
                                className="post-schedule"
                                errorMessage={
                                    (formikBag.errors.data as FormikValues['data'])?.post_time_window?.start ||
                                    (formikBag.errors.data as FormikValues['data'])?.post_time_window?.end
                                }
                            />
                        )}
                        <Text scale="bodyL700" textAlign="left">
                            {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                        </Text>
                        <Text scale="caption400" textAlign="left">
                            {newT('submission.review.commentsForPartiposterSubtext')}
                        </Text>
                        <Inputs.TextArea
                            className="expanding-input"
                            name="comment"
                            placeholder={newT('placeholderText.enterCommentForPartiposter')}
                            minTextAreaHeight={120}
                            maxTextAreaHeight={210}
                        />
                    </ScrollableBox>
                    <Box sx={{ textAlign: 'right' }} mt={spacing[200]}>
                        <RectangleButton
                            type="submit"
                            size="m"
                            color="primary"
                            label={newT('button.approve').toUpperCase()}
                            isDisabled={
                                !(
                                    formikBag.values.comment &&
                                    (!showFileInput || formikBag.values.data.files) &&
                                    (!showPostTimeInput ||
                                        (showPostTimeInput &&
                                            ((formikBag.values.data.post_time_window &&
                                                formikBag.values.data.post_time_window.start &&
                                                formikBag.values.data.post_time_window.end) ||
                                                !formikBag.values.data.post_time_window ||
                                                (!formikBag.values.data.post_time_window.start &&
                                                    !formikBag.values.data.post_time_window.end))))
                                ) ||
                                retryProgressUuids.includes(submissionProgress.uuid) ||
                                rewriteProgressUuids.includes(submissionProgress.uuid)
                            }
                        />
                    </Box>
                    <ScrollToError />
                    <AutoSaveReviewData uuid={submissionProgress.uuid} decisionEditor={DECISION_EDITOR_ENUM.APPROVE} />
                </Form>
            )}
        </Formik>
    )
}

const EditSubmission: React.FC<any> = ({ task, submissionUuid, submissionProgress, handleEditTagChange }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const editTagGroupOptions = useSelector(selectEditTagGroupOptions())
    const [submissionEditTagGroupOptions, setSubmissionEditTagGroupOptions] =
        useState<DisplayGroupedOption[]>(editTagGroupOptions)
    const selectedReviewData = useSelector(
        selectSelectedReviewData(submissionProgress.uuid, DECISION_EDITOR_ENUM.REQUEST_EDITS),
    )
    const activePageNum = useSelector(selectActivePageNum)
    const selectedStep = useSelector(selectSidebarStep)
    const submissionListParams = useSelector(selectSubmissionListParams)

    const initialValues = useMemo(() => {
        if (selectedReviewData) {
            return selectedReviewData
        }
        return {
            comment: '',
            tag_uuids: submissionProgress && submissionProgress.tag_uuids ? submissionProgress.tag_uuids : [],
        }
    }, [selectedReviewData, submissionProgress])

    const handleEdit = useCallback(
        (comment: any, tag_uuids: string[]) => {
            if (selectedCampaignUuid) {
                dispatch(
                    requestEditsForSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionProgressUuid: submissionProgress.uuid,
                        reason: comment,
                        tag_uuids,
                        getSubmissionListPayload: {
                            offset: activePageNum * MAX_PAGE_SIZE,
                            stepUuid: selectedStep?.uuid,
                            ...submissionListParams,
                        },
                    }),
                )
                dispatch(clearSelectedReviewData(submissionProgress.uuid))
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(
                newT('errorMessage.validation.enterRequestEditReason', 'Please enter a reason for the Partiposter'),
            ),
        tag_uuids: yup
            .array()
            .nullable()
            .required(
                newT('errorMessage.validation.selectRequestEditReason', 'Please select a reason for edits above.'),
            ),
    })
    const onTagChange = (setFieldValue: any) => (options: TagOption[]) => {
        handleEditTagChange(options, setSubmissionEditTagGroupOptions, (values: any) => {
            setFieldValue('comment', values.comment)
            setFieldValue('tag_uuids', values.tag_uuids)
        })
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={(values: { comment: string | null; tag_uuids: [] }, actions) => {
                const retTagUuids = values.tag_uuids.map((tag: TagOption) => tag.uuid)
                handleEdit(values.comment, retTagUuids)
                actions.setSubmitting(false)
            }}
            render={props => {
                return (
                    <Form>
                        <FlexBox overflowX="hidden" flexDirection="column" gap={spacing[200]}>
                            <ScrollableBox>
                                <Text scale="bodyL700" textAlign="left">
                                    {newT(
                                        'submission.review.requestEditForSubmissionTitle',
                                        'Request Edits this submission',
                                    )}
                                </Text>
                                <Box py={spacing[300]} px={spacing[600]}>
                                    <Text scale="caption400" color="warning" textAlign="center">
                                        {newT(
                                            'submission.review.requestEditCommentInstruction',
                                            'The Partiposter will be able to resubmit according to your comments.',
                                        )}
                                    </Text>
                                </Box>
                                <Restricted permissions={[CampaignSubmission.ReviewCaptionWithAi]}>
                                    <AiReview reviewMode={'RequestEdit'} submissionProgress={submissionProgress} />
                                </Restricted>
                                <Dropdown
                                    label={newT('submission.review.reasonsForRequestEditTitle')}
                                    subtext={newT('submission.review.reasonsForRejectionSubtitle')}
                                    placeholder={newT('placeholderText.dropdown.requestEditsReason')}
                                    onTagChange={onTagChange(props.setFieldValue)}
                                    tag_uuids={props.values.tag_uuids.map((tag: TagOption) => tag.uuid)}
                                    displayOptions={submissionEditTagGroupOptions}
                                />
                                <Inputs.TextArea
                                    className="expanding-input"
                                    name="comment"
                                    placeholder={newT(
                                        'submission.review.reasonsForRequestEditSubtitle',
                                        'Select a reason above and edit the comment here.',
                                    )}
                                    minTextAreaHeight={120}
                                    maxTextAreaHeight={210}
                                    isDisabled={props.values.tag_uuids ? props.values.tag_uuids.length === 0 : true}
                                />
                            </ScrollableBox>
                            <Box width="100%" sx={{ textAlign: 'right' }}>
                                <ButtonPrimary
                                    type="submit"
                                    themeColor="orange"
                                    disabled={props.values.tag_uuids ? props.values.tag_uuids.length === 0 : true}
                                >
                                    {newT('button.requestEdits')}
                                </ButtonPrimary>
                            </Box>

                            <ScrollToError />
                            <AutoSaveReviewData
                                uuid={submissionProgress.uuid}
                                decisionEditor={DECISION_EDITOR_ENUM.REQUEST_EDITS}
                            />
                        </FlexBox>
                    </Form>
                )
            }}
        />
    )
}

const RejectSubmission: React.FC<any> = ({
    task,
    submissionUuid,
    submissionProgress,
    handleRejectTagChange,
    isInsideBatch,
}) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const rejectionTagGroupOptions = useSelector(selectRejectionTagGroupOptions())
    const [submissionRejectTagGroupOptions, setSubmissionRejectTagGroupOptions] =
        useState<DisplayGroupedOption[]>(rejectionTagGroupOptions)
    const selectedReviewData = useSelector(
        selectSelectedReviewData(submissionProgress.uuid, DECISION_EDITOR_ENUM.REJECT),
    )
    const activePageNum = useSelector(selectActivePageNum)
    const selectedStep = useSelector(selectSidebarStep)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const initialValues = useMemo(() => {
        if (selectedReviewData) {
            return selectedReviewData
        }
        return {
            comment: '',
            tag_uuids: submissionProgress && submissionProgress.tag_uuids ? submissionProgress.tag_uuids : [],
        }
    }, [selectedReviewData, submissionProgress])
    const getSubmissionListPayload = {
        offset: activePageNum * MAX_PAGE_SIZE,
        stepUuid: selectedStep?.uuid,
        ...submissionListParams,
    }

    const handleReject = useCallback(
        (comment: any, tag_uuids: string[]) => {
            if (selectedCampaignUuid) {
                dispatch(
                    rejectSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionProgressUuid: submissionProgress.uuid,
                        reason: comment,
                        tag_uuids,
                        getSubmissionListPayload,
                    }),
                )
                dispatch(clearSelectedReviewData(submissionProgress.uuid))
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(
                newT('errorMessage.validation.enterRequestEditReason', 'Please enter a reason for the Partiposter'),
            ),
        tag_uuids: yup
            .array()
            .nullable()
            .required(
                newT('errorMessage.validation.selectRejectReason', 'Please select a reason for rejection above.'),
            ),
    })
    const onTagChange = (setFieldValue: any) => (options: TagOption[]) => {
        handleRejectTagChange(options, setSubmissionRejectTagGroupOptions, (values: any) => {
            setFieldValue('comment', values.comment)
            setFieldValue('tag_uuids', values.tag_uuids)
        })
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={(values: { comment: string | null; tag_uuids: [] }, actions) => {
                const retTagUuids = values.tag_uuids.map((tag: TagOption) => tag.uuid)
                if (isInsideBatch) {
                    handleReject(values.comment, retTagUuids)
                } else {
                    if (selectedCampaignUuid) {
                        dispatch(
                            setConfirmRejectDataInWaitingList({
                                campaignUuid: selectedCampaignUuid,
                                submissionUuid:
                                    selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                                        ? submissionUuid
                                        : undefined,
                                submissionProgressUuid: submissionProgress.uuid,
                                reason: values.comment ? values.comment : '',
                                tag_uuids: retTagUuids,
                                getSubmissionListPayload,
                            }),
                        )
                        dispatch(setReviewModalType(REVIEW_MODAL_TYPE.REJECT_SUBMISSION_IN_WAITING_LIST))
                        dispatch(setReviewModalOpen(true))
                    }
                }
                actions.setSubmitting(false)
            }}
            render={props => {
                return (
                    <Form>
                        <FlexBox overflowX="hidden" flexDirection="column" gap={spacing[200]}>
                            <ScrollableBox>
                                <Text scale="bodyL700" textAlign="left">
                                    {newT('submission.review.rejectSubmissionTitle', 'Reject this submission')}
                                </Text>

                                <Box py={spacing[300]} px={spacing[600]}>
                                    <Text scale="caption400" color="error" textAlign="center">
                                        {newT(
                                            'submission.review.rejectSubmissionWarning',
                                            'Warning: After you reject this submission, the partiposter will not be able to join this campaign again.',
                                        )}
                                    </Text>
                                </Box>
                                <Dropdown
                                    label={newT('submission.review.reasonsForRejectionTitle')}
                                    subtext={newT('submission.review.reasonsForRejectionSubtitle')}
                                    placeholder={newT('placeholderText.dropdown.rejectReason')}
                                    onTagChange={onTagChange(props.setFieldValue)}
                                    tag_uuids={props.values.tag_uuids.map((tag: TagOption) => tag.uuid)}
                                    displayOptions={submissionRejectTagGroupOptions}
                                />
                                <Inputs.TextArea
                                    className="expanding-input"
                                    name="comment"
                                    placeholder={newT(
                                        'submission.review.reasonsForRequestEditSubtitle',
                                        'Select a reason above and edit the comment here.',
                                    )}
                                    minTextAreaHeight={120}
                                    maxTextAreaHeight={210}
                                    isDisabled={props.values.tag_uuids && props.values.tag_uuids.length == 0}
                                />
                            </ScrollableBox>
                            <Box width="100%" sx={{ textAlign: 'right' }}>
                                <ButtonPrimary
                                    type="submit"
                                    themeColor="red"
                                    disabled={props.values.tag_uuids ? props.values.tag_uuids.length === 0 : true}
                                >
                                    {newT('button.reject')}
                                </ButtonPrimary>
                            </Box>
                            <ScrollToError />
                            <AutoSaveReviewData
                                uuid={submissionProgress.uuid}
                                decisionEditor={DECISION_EDITOR_ENUM.REJECT}
                            />
                        </FlexBox>
                    </Form>
                )
            }}
        />
    )
}

const ScrollableBox = styled.div`
    box-sizing: border-box;
    overflow-y: scroll;
    overflow-x: hidden;
    background: ${coreColors.white50};
    height: 583px;
    width: 400px;
    padding: ${spacing[400]};

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${coreColors.grey700};
        border-radius: 4px;
    }

    .expanding-input {
        margin: 8px 0;
        &::-webkit-scrollbar {
            width: 4px;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${coreColors.grey700};
            border-radius: 4px;
        }
    }

    .post-schedule {
        margin-bottom: 24px;
    }
`
const FlexBox = styled.div<CSSProperties>`
    box-sizing: border-box;
    display: flex;
    flex-direction: ${p => p.flexDirection || 'row'};
    align-items: ${p => p.alignItems || 'flex-start'};
    justify-content: ${p => p.justifyContent || 'flex-start'};
    gap: ${p => p.gap || '0px'};
    overflow: ${p => p.overflow || 'visible'};
    overflow-x: ${p => p.overflowX || 'visible'};
    overflow-y: ${p => p.overflowY || 'visible'};
    flex-wrap: ${p => p.flexWrap || 'nowrap'};
    margin-bottom ${p => p.marginBottom || '0px'};
`

export default ReviewSubmission
